import axios from "axios";
import Config from "../../Config";

class RolesRequest {
  static baseUrl = `${Config.backendDomain}vendor`;

  static getHeaders() {
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${RolesRequest.getToken()}`,
      },
    };
  }

  static getToken() {
    return localStorage.getItem("dealboxx_token");
  }

  //Todo => Get all team members/users
  static async getAllTeamMembers() {
    try {
      const response = await axios.get(
        `${RolesRequest.baseUrl}/team/member`,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        const transformedData = response.data.data.data.map((member) => ({
          id: member.id,
          vendorSlug: member.vendor_slug,
          fullName: member.full_name,
          email: member.email,
          phone: member.phone,
          role: member.role,
          status: "Active",
          isMainVendor: member.is_main_vendor,
          permissions: member.permissions,
          createdOn: member.created_at,
          createdAt: member.created_at,
        }));

        return {
          success: true,
          data: transformedData,
          message:
            response.data.data.reason || "Team members fetched successfully",
        };
      }

      return {
        success: false,
        message: response.data?.data?.reason || "Failed to fetch team members",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while fetching team members",
      };
    }
  }

  //Todo => Create a new team member (backend handles invitation automatically)
  static async createTeamMember(userData) {
    try {
      const payload = {
        full_name: userData.fullName,
        email: userData.email,
        phone_number: userData.phone,
        role_slug: userData.roleSlug,
      };

      const response = await axios.post(
        `${RolesRequest.baseUrl}/team/member`,
        payload,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "001") {
        const transformedMember = {
          id: response.data.data.data.id,
          vendorSlug: response.data.data.data.vendor_slug,
          fullName: response.data.data.data.brand_name,
          email: response.data.data.data.email,
          phone: response.data.data.data.phone_number,
          role: "New Member",
          status: "Active",
          isMainVendor: response.data.data.data.is_main_vendor,
          permissions: {},
          createdOn: response.data.data.data.created_at,
          createdAt: response.data.data.data.created_at,
        };
        return {
          success: true,
          data: transformedMember,
          message:
            response.data.data.reason || "Team member created successfully",
        };
      }
      return {
        success: false,
        message: response.data?.data?.reason || "Failed to create team member",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while creating team member",
      };
    }
  }

  //Todo => Update team member (includes both details and permissions)
  static async updateTeamMember(userData) {
    try {
      const payload = userData.full_name
        ? userData
        : {
            full_name: userData.fullName,
            phone_number: userData.phone,
            email: userData.email,
            role_slug: userData.roleSlug,
            vendor_slug: userData.vendorSlug,
          };
      const response = await axios.put(
        `${RolesRequest.baseUrl}/update/team_member`,
        payload,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        const transformedData = {
          id: response.data.data.data.id,
          vendorSlug: response.data.data.data.vendor_slug,
          fullName: response.data.data.data.full_name,
          email: response.data.data.data.email,
          phone: response.data.data.data.phone,
          role: response.data.data.data.role,
          status: "Active",
          isMainVendor: response.data.data.data.is_main_vendor,
          permissions: response.data.data.data.permissions || [],
          createdAt: response.data.data.data.created_at,
          updatedAt: response.data.data.data.updated_at,
        };

        return {
          success: true,
          data: transformedData,
          message:
            response.data.data.reason || "Team member updated successfully",
        };
      }
      if (response.data?.data?.status_code === "422") {
        return {
          success: false,
          message: response.data?.data?.reason || "Validation Error",
          statusCode: response.data?.data?.status_code,
          errors: response.data?.data?.errors || {},
        };
      }

      return {
        success: false,
        message:
          response.data?.data?.reason ||
          response.data?.data?.message ||
          "Failed to update team member",
        statusCode: response.data?.data?.status_code,
      };
    } catch (error) {
      if (error.response?.data?.data?.status_code === "422") {
        return {
          success: false,
          message: error.response?.data?.data?.reason || "Validation Error",
          statusCode: error.response?.data?.data?.status_code,
          errors: error.response?.data?.data?.errors || {},
        };
      }

      return {
        success: false,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "An error occurred while updating team member",
        statusCode: error.response?.data?.data?.status_code || "NETWORK_ERROR",
      };
    }
  }

  //Todo => Delete team member
  static async deleteTeamMember(vendorSlug) {
    try {
      const response = await axios.post(
        `${RolesRequest.baseUrl}/destroy/team_member`,
        { vendor_slug: vendorSlug },
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          message: "Team member deleted successfully",
        };
      }
      return {
        success: false,
        message: response.data?.data?.reason || "Failed to delete team member",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while deleting team member",
      };
    }
  }

  //Todo => Activate or deactivate team member
  static async updateTeamMemberStatus(userId, status) {
    try {
      const payload = {
        status: status,
      };

      const response = await axios.patch(
        `${RolesRequest.baseUrl}/team/users/${userId}/status`,
        payload,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "001") {
        return {
          success: true,
          data: response.data.data.data,
          message: `Team member ${
            status === "active" ? "activated" : "deactivated"
          } successfully`,
        };
      }

      return {
        success: false,
        message:
          response.data?.data?.reason || "Failed to update team member status",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while updating team member status",
      };
    }
  }
  //Todo => Get available roles and their default permissions
  static async getAvailableRoles() {
    try {
      const response = await axios.get(
        `${RolesRequest.baseUrl}/roles`,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data,
          message: "Roles fetched successfully",
        };
      }

      return {
        success: false,
        message: response.data?.data?.reason || "Failed to fetch roles",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while fetching roles",
      };
    }
  }

  //Todo => Get team member by ID
  static async getTeamMemberById(userId) {
    try {
      const response = await axios.get(
        `${RolesRequest.baseUrl}/team/users/${userId}`,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data,
          message: "Team member fetched successfully",
        };
      }
      return {
        success: false,
        message: response.data?.data?.reason || "Failed to fetch team member",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while fetching team member",
      };
    }
  }

  //Todo => Get all available system permissions
  static async getSystemPermissions() {
    try {
      const response = await axios.get(
        `${RolesRequest.baseUrl}/permissions`,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data,
          message: "System permissions fetched successfully",
        };
      }

      return {
        success: false,
        message:
          response.data?.data?.reason || "Failed to fetch system permissions",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while fetching system permissions",
      };
    }
  }

  //Todo => Get all custom roles created by the vendor
  static async getCustomRoles() {
    try {
      const response = await axios.get(
        `${RolesRequest.baseUrl}/roles`,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data,
          message: "Custom roles fetched successfully",
        };
      }

      return {
        success: false,
        message: response.data?.data?.reason || "Failed to fetch custom roles",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while fetching custom roles",
      };
    }
  }

  //Todo => Create a new custom role
  static async createCustomRole(roleData) {
    try {
      const payload = {
        name: roleData.name,
        permissions: roleData.permissions,
      };

      const response = await axios.post(
        `${RolesRequest.baseUrl}/roles`,
        payload,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data,
          message: "Custom role created successfully",
        };
      }

      return {
        success: false,
        message: response.data?.data?.reason || "Failed to create custom role",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while creating custom role",
      };
    }
  }

  //Todo => Update an existing custom role
  static async updateCustomRole(roleSlug, roleData) {
    try {
      const payload = {
        name: roleData.name,
        permissions: roleData.permissions,
        role_slug: roleSlug,
      };

      const response = await axios.put(
        `${RolesRequest.baseUrl}/roles/${roleSlug}`,
        payload,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data,
          message: "Custom role updated successfully",
        };
      }

      return {
        success: false,
        message: response.data?.data?.reason || "Failed to update custom role",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while updating custom role",
      };
    }
  }

  //Todo => Delete a custom role
  static async deleteCustomRole(roleId) {
    try {
      const response = await axios.delete(
        `${RolesRequest.baseUrl}/roles/${roleId}`,
        RolesRequest.getHeaders()
      );

      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          message: "Custom role deleted successfully",
        };
      }
      return {
        success: false,
        message: response.data?.data?.reason || "Failed to delete custom role",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while deleting custom role",
      };
    }
  }

  //Todo => Check if role can be deleted
  static async checkRoleDeletable(roleId) {
    try {
      const response = await axios.get(
        `${RolesRequest.baseUrl}/roles/${roleId}/check-deletable`,
        RolesRequest.getHeaders()
      );
      if (response.data?.data?.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data,
          message: "Role deletion check completed",
        };
      }
      return {
        success: false,
        message:
          response.data?.data?.reason || "Failed to check role deletion status",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while checking role deletion status",
      };
    }
  }
}

export default RolesRequest;
